import type { PopphinSaveSystem } from "./PopphinSaveSystem";
import { GENERIC_ACHIEVEMENTS } from "@gametype/match_learning/AchievementRules";
import type { MatchSessionState, MatchSessionResult } from "@gametype/match_learning/types";
import achievementsData from "@config/data/achievements.json";

/**
 * Popphin-specific achievement checker on top of generic gametype rules.
 * Evaluates achievement conditions after a session completes and records new unlocks.
 */
export class AchievementManager {
  constructor(private readonly save: PopphinSaveSystem) {}

  async checkOnSessionComplete(
    state: MatchSessionState,
    result: MatchSessionResult
  ): Promise<string[]> {
    const already = (await this.save.load()).unlockedAchievements;
    const newlyUnlocked: string[] = [];

    for (const a of GENERIC_ACHIEVEMENTS) {
      if (a.check(state, result) && !already.includes(a.id) && !newlyUnlocked.includes(a.id)) {
        newlyUnlocked.push(a.id);
      }
    }

    for (const a of achievementsData) {
      if (already.includes(a.id) || newlyUnlocked.includes(a.id)) continue;
      const cond = a.condition;
      if (cond === "correctCount>=1" && result.correctCount >= 1) newlyUnlocked.push(a.id);
      else if (cond === "streak>=5" && state.bestStreak >= 5) newlyUnlocked.push(a.id);
      else if (cond === "streak>=10" && state.bestStreak >= 10) newlyUnlocked.push(a.id);
      else if (cond === "perfectSession" && result.wrongCount === 0 && result.correctCount > 0) {
        newlyUnlocked.push(a.id);
      } else if (cond === "codexComplete" && result.itemsMatched.length >= 30) {
        newlyUnlocked.push(a.id);
      }
    }

    return newlyUnlocked;
  }
}
